import { useState } from "react";
import { Link } from "react-router-dom";
import { Quotes, CaretDown, CaretUp } from "@phosphor-icons/react";
import { Badge } from "./ui/badge";

/**
 * Evidence Cards — replaces the "Most Recent Signals" raw text dump.
 *
 * Surfaces the top 3-5 verbatim phrases extracted from recent signals,
 * each with an interpretive line and one supporting quote. Raw signals
 * are tucked behind a disclosure for analyst-mode viewing.
 *
 * Style: intelligence-grade. Each card reads like a desk-officer note.
 */
export default function EvidenceCards({ evidenceCards, recentSignals = [], streamId }) {
    const [showRaw, setShowRaw] = useState(false);
    const cards = (evidenceCards || []).filter((c) => c.phrase);

    if (cards.length === 0 && recentSignals.length === 0) {
        return (
            <section data-testid="stream-evidence" className="mb-12">
                <div className="flex items-baseline gap-3 mb-5">
                    <div className="overline">Evidence</div>
                    <div className="h-px bg-border flex-1" />
                </div>
                <p className="text-sm text-muted-foreground italic">No signals collected yet — trigger an ingest.</p>
            </section>
        );
    }

    return (
        <section data-testid="stream-evidence" className="mb-12">
            <div className="flex items-baseline gap-3 mb-5">
                <div className="overline">Evidence</div>
                <div className="h-px bg-border flex-1" />
                <span className="font-mono text-[11px] text-muted-foreground tracking-wider">
                    {cards.length === 1 ? "1 recurring phrase" : `${cards.length} recurring phrases`}
                </span>
            </div>

            {cards.length === 0 ? (
                <p className="text-sm text-muted-foreground italic mb-6">
                    No recurring language detected yet — synthesize a briefing once enough signals accumulate.
                </p>
            ) : (
                <ul className="space-y-7">
                    {cards.map((c, i) => (
                        <li
                            key={i}
                            data-testid={`evidence-card-${i}`}
                            className="border-l-2 border-accent pl-6 max-w-[68ch]"
                        >
                            <div className="flex items-baseline gap-3 mb-2">
                                <span className="font-mono text-[10px] text-muted-foreground tabular-nums">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <Quotes size={14} weight="fill" className="text-accent shrink-0" />
                                <span className="font-display font-semibold text-[19px] leading-snug italic">
                                    {c.phrase}
                                </span>
                            </div>
                            <div className="ml-7">
                                {c.interpretation && (
                                    <p className="editorial text-[14px] text-foreground/85 leading-relaxed mb-2">
                                        {c.interpretation}
                                    </p>
                                )}
                                <div className="flex items-baseline gap-3 text-[11px] font-mono text-muted-foreground tracking-wider">
                                    <span>recurring · {c.recurrence}× across analyses</span>
                                    {c.platform && (
                                        <>
                                            <span className="text-border">·</span>
                                            <span>first observed on {c.platform}</span>
                                        </>
                                    )}
                                </div>
                                {c.verbatim && (
                                    <p className="mt-3 text-[13px] text-foreground/70 italic line-clamp-2 leading-snug">
                                        "…{c.verbatim}…"
                                    </p>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {/* Analyst-mode disclosure: raw signals */}
            {recentSignals.length > 0 && (
                <div className="mt-9 pt-5 border-t border-border">
                    <button
                        type="button"
                        data-testid="evidence-toggle-raw"
                        onClick={() => setShowRaw((v) => !v)}
                        className="text-[11px] font-mono uppercase tracking-[0.18em] text-muted-foreground hover:text-primary inline-flex items-center gap-1.5"
                    >
                        {showRaw ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />}
                        {showRaw ? "Hide" : "Show"} raw signals · {Math.min(recentSignals.length, 12)}
                        <span className="ml-2 text-muted-foreground">analyst mode</span>
                    </button>
                    {showRaw && (
                        <ul className="mt-5 space-y-3" data-testid="evidence-raw-list">
                            {recentSignals.slice(0, 12).map((c) => (
                                <li key={c.id} className="border-l-2 border-border pl-4 py-1 hover:border-primary transition-colors">
                                    <div className="text-[13px] leading-relaxed text-foreground/80 line-clamp-3">{c.raw_text}</div>
                                    <div className="flex gap-2 mt-1.5 text-[10px] font-mono text-muted-foreground">
                                        <Badge variant="outline" className="rounded-sm text-[9px] py-0 px-1.5">{c.platform}</Badge>
                                        <span>@{c.author_handle || "anon"}</span>
                                        <span>·</span>
                                        <span>{(c.imported_at || "").slice(0, 10)}</span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                    {streamId && (
                        <Link
                            to={`/signals?stream=${streamId}`}
                            className="text-[11px] font-mono uppercase tracking-[0.18em] text-muted-foreground hover:text-primary mt-3 inline-block"
                        >
                            Open in Raw Signals →
                        </Link>
                    )}
                </div>
            )}
        </section>
    );
}
